import { useDispatch, useSelector } from 'react-redux';
import { fetchInboxUnreadCount, markThreadRead } from 'src/services/client';
import {
  InboxUnreadCountState,
  setUnreadCountAction,
} from 'src/store/slices/inboxUnreadCountSlice';

interface InboxUnreadCountSliceState {
  inboxUnreadCount: InboxUnreadCountState;
}

interface UnreadCountResponseBody {
  message: string;
  data: { unreadCount: number };
}

interface MarkThreadReadResult {
  ok: boolean;
}

export interface UseInboxUnreadCount {
  unreadCount: number;
  nextRefreshAt: number | null;
  refreshUnreadCount: () => Promise<void>;
  markThreadReadAndRefresh: (
    referenceId: string,
    referenceType: string
  ) => Promise<MarkThreadReadResult>;
}

const readUnreadCountFromResponse = async (
  response: any
): Promise<number | null> => {
  if (response.isError || !response.ok) {
    return null;
  }

  const body: UnreadCountResponseBody = await response.json();

  const count: number | undefined = body?.data?.unreadCount;

  if (typeof count !== 'number') {
    return null;
  }

  return count;
};

export const useInboxUnreadCount = (): UseInboxUnreadCount => {
  const dispatch = useDispatch();

  const unreadCount: number = useSelector(
    (state: InboxUnreadCountSliceState) => state.inboxUnreadCount.unreadCount
  );

  const nextRefreshAt: number | null = useSelector(
    (state: InboxUnreadCountSliceState) => state.inboxUnreadCount.nextRefreshAt
  );

  const applyUnreadCount = (count: number | null, source: string): void => {
    if (count === null) {
      console.warn(
        `[useInboxUnreadCount] ${source} returned no usable count; keeping last value.`
      );
      return;
    }

    dispatch(setUnreadCountAction(count));
  };

  const refreshUnreadCount = async (): Promise<void> => {
    const response: any = await fetchInboxUnreadCount();
    const count: number = await readUnreadCountFromResponse(response);

    applyUnreadCount(count, 'unread-count fetch');
  };

  const markThreadReadAndRefresh = async (
    referenceId: string,
    referenceType: string
  ): Promise<MarkThreadReadResult> => {
    const response: any = await markThreadRead(referenceId, referenceType);
    const count: number = await readUnreadCountFromResponse(response);

    applyUnreadCount(count, 'mark-thread-read');

    return { ok: count !== null };
  };

  return {
    unreadCount,
    nextRefreshAt,
    refreshUnreadCount,
    markThreadReadAndRefresh,
  };
};